<!-- src/app/features/wizard/form/form-field/form-field.component.html -->
<div [formGroup]="formGroup" class="form-field-wrapper" [class.field-array]="isArray" [class.field-complex]="isComplex">

  <!-- Error state for nested shapes -->
  <div *ngIf="nestedShapeError" class="error-overlay">
    <mat-icon color="warn">error_outline</mat-icon>
    <span>{{ nestedShapeError }}</span>
  </div>

  <!-- Loading state for nested shapes -->
  <div *ngIf="loadingNestedShape" class="loading-overlay">
    <mat-spinner diameter="30"></mat-spinner>
    <span>Verschachtelte Felder werden geladen...</span>
  </div>

  <!-- Simple Field (Not Array, Not Complex) -->
  <ng-container *ngIf="!isArray && !isComplex">
    <div class="simple-field-container" *ngIf="formGroup.get(fieldControlName)">

      <!-- Text, Number, Date Inputs -->
      <mat-form-field appearance="outline" class="full-width" *ngIf="!hasEnum && inputType !== 'checkbox'">
        <mat-label>{{ fieldLabel }}</mat-label>
        <input matInput [type]="inputType" [formControlName]="fieldControlName"
          [placeholder]="normalizedField.description || ''" [step]="inputType === 'number' ? 'any' : undefined"
          [required]="normalizedField.required">
        <mat-hint *ngIf="normalizedField.description">{{ normalizedField.description }}</mat-hint>
        <mat-error *ngIf="formGroup.get(fieldControlName)?.hasError('required')">
          {{ fieldLabel }} ist erforderlich
        </mat-error>
      </mat-form-field>

      <!-- Checkbox (Boolean) -->
      <div class="checkbox-container" *ngIf="inputType === 'checkbox'">
        <mat-checkbox [formControlName]="fieldControlName" color="primary">
          {{ fieldLabel }}
          <span *ngIf="normalizedField.required" class="required-indicator">*</span>
        </mat-checkbox>
        <p class="field-hint" *ngIf="normalizedField.description">{{ normalizedField.description }}</p>
      </div>

      <!-- Select (Enum) -->
      <mat-form-field appearance="outline" class="full-width" *ngIf="hasEnum">
        <mat-label>{{ fieldLabel }}</mat-label>
        <mat-select [formControlName]="fieldControlName" [required]="normalizedField.required">
          <mat-option value="">{{ normalizedField.description || 'Auswählen...' }}</mat-option>
          <mat-option *ngFor="let value of normalizedField.allowedTypes" [value]="value">
            {{ value }}
          </mat-option>
        </mat-select>
        <mat-hint *ngIf="normalizedField.description">{{ normalizedField.description }}</mat-hint>
      </mat-form-field>
    </div>
  </ng-container>

  <!-- Registration Number (Not Array) -->
  <div *ngIf="!isArray && isRegistrationNumber && getNestedFormGroup() as nestedGroup"
    class="registration-field-container">
    <mat-form-field appearance="outline" class="full-width">
      <mat-label>Registration Number Type</mat-label>
      <mat-select [value]="selectedSingleRegistrationType" (selectionChange)="onRegistrationTypeChange($event.value)">
        <mat-option *ngFor="let opt of registrationOptions" [value]="opt.value">{{opt.label}}</mat-option>
      </mat-select>
    </mat-form-field>

    <div *ngIf="selectedSingleRegistrationType" [formGroup]="nestedGroup" class="registration-value-input">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>{{ getSelectedRegistrationLabel() }}</mat-label>
        <input matInput [formControlName]="getRegistrationControlName(selectedSingleRegistrationType)" required>
      </mat-form-field>
    </div>
  </div>

  <!-- Complex Field (Not Array) - Using Expansion Panel -->
  <div
    *ngIf="!isArray && isComplex && !isRegistrationNumber && nestedShape && !loadingNestedShape && getNestedFormGroup() as nestedGroup"
    class="complex-field-container">
    <mat-expansion-panel [expanded]="false">
      <mat-expansion-panel-header>
        <mat-panel-title>
          <div class="title-with-icon">
            <mat-icon color="primary" class="header-icon">account_tree</mat-icon>
            <span class="complex-label">{{ fieldLabel }}</span>
            <span *ngIf="normalizedField.required" class="required-indicator">*</span>
          </div>
        </mat-panel-title>
        <mat-panel-description *ngIf="normalizedField.description">
          {{ normalizedField.description | slice:0:50 }}{{ normalizedField.description.length > 50 ? '...' : '' }}
        </mat-panel-description>
      </mat-expansion-panel-header>

      <div class="nested-fields-indent" [formGroup]="nestedGroup">
        <app-form-field *ngFor="let nestedField of getNestedFields()" [field]="nestedField" [formGroup]="nestedGroup"
          [parentPath]="fullPath" [initialValue]="getNestedInitialValue(nestedField, initialValue)"
          [parentShapes]="currentShapes">
        </app-form-field>
      </div>
    </mat-expansion-panel>
  </div>

  <!-- Array Field -->
  <div *ngIf="isArray && formArray" [formArrayName]="fieldControlName" class="array-field-container">
    <div class="array-header">
      <div class="array-title-group">
        <mat-icon color="primary">list_alt</mat-icon>
        <h3 class="array-label">{{ fieldLabel }}</h3>
        <span class="array-count-badge" *ngIf="formArray.length > 0">{{ formArray.length }}</span>
      </div>
      <p class="field-description" *ngIf="normalizedField.description">{{ normalizedField.description }}</p>
    </div>

    <div class="array-items-list">
      <div *ngFor="let item of formArray.controls; let i = index" class="array-item-wrapper">

        <!-- Registration Number Array Item -->
        <div *ngIf="isRegistrationNumber" [formGroupName]="i" class="registration-array-item">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Registration Number Type</mat-label>
            <mat-select [value]="selectedRegistrationTypes[i]"
              (selectionChange)="onRegistrationTypeChange($event.value, i)">
              <mat-option *ngFor="let opt of registrationOptions" [value]="opt.value">{{opt.label}}</mat-option>
            </mat-select>
          </mat-form-field>

          <div *ngIf="selectedRegistrationTypes[i]" class="registration-value-input">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ getSelectedRegistrationLabel(i) }}</mat-label>
              <input matInput [formControlName]="getRegistrationControlName(selectedRegistrationTypes[i], i)" required>
            </mat-form-field>
          </div>

          <button mat-icon-button color="warn" *ngIf="canRemove" (click)="removeArrayItem(i)" type="button"
            title="Element entfernen">
            <mat-icon>delete_outline</mat-icon>
          </button>
        </div>

        <!-- Complex Array Item (Nested Shape) - Using Expansion Panel -->
        <mat-expansion-panel *ngIf="isComplex && !isRegistrationNumber" [expanded]="true" class="array-expansion-item">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="title-with-index">
                <span class="item-index">{{ i + 1 }}</span>
                <span class="complex-label">{{ fieldLabel }} Element</span>
              </div>
            </mat-panel-title>

            <!-- Delete Button in Header for Complex Items -->
            <button mat-icon-button color="warn" *ngIf="canRemove"
              (click)="removeArrayItem(i); $event.stopPropagation()" type="button" class="remove-btn-header"
              title="Element entfernen">
              <mat-icon>delete_forever</mat-icon>
            </button>
          </mat-expansion-panel-header>

          <div [formGroupName]="i" class="nested-fields-indent">
            <app-form-field *ngFor="let nestedField of getNestedFields()" [field]="nestedField"
              [formGroup]="$any(formArray.at(i))" [parentPath]="fullPath + '[' + i + ']'"
              [initialValue]="getNestedInitialValue(nestedField, initialValue ? initialValue[i] : undefined)"
              [parentShapes]="currentShapes">
            </app-form-field>
          </div>
        </mat-expansion-panel>

        <!-- Simple Array Item - Using Card -->
        <div *ngIf="!isComplex" class="array-item-card">
          <div class="array-item-index">{{ i + 1 }}</div>

          <div class="array-item-content">
            <mat-form-field appearance="outline" class="full-width" *ngIf="!hasEnum && inputType !== 'checkbox'">
              <input matInput [type]="inputType" [formControlName]="i"
                [placeholder]="normalizedField.description || fieldLabel"
                [step]="inputType === 'number' ? 'any' : undefined">
            </mat-form-field>

            <mat-checkbox [formControlName]="i" *ngIf="inputType === 'checkbox'" color="primary">
              Element {{ i + 1 }}
            </mat-checkbox>

            <mat-form-field appearance="outline" class="full-width" *ngIf="hasEnum">
              <mat-select [formControlName]="i">
                <mat-option *ngFor="let value of normalizedField.allowedTypes!" [value]="value">
                  {{ value }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <button mat-icon-button color="warn" *ngIf="canRemove" (click)="removeArrayItem(i)" type="button"
            class="remove-btn" title="Element entfernen">
            <mat-icon>delete_outline</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <div class="array-actions">
      <button mat-raised-button color="primary" *ngIf="canAddMore" type="button" (click)="addArrayItem()"
        class="add-btn">
        <mat-icon>add_circle_outline</mat-icon>
        {{ fieldLabel || 'Element' }} hinzufügen
      </button>

      <div class="array-constraints">
        <span *ngIf="minCount > 0" class="constraint-chip">Min: {{ minCount }}</span>
        <span *ngIf="normalizedField.maxCount !== null" class="constraint-chip">Max: {{ normalizedField.maxCount
          }}</span>
      </div>
    </div>
  </div>
</div>